import { FC, memo, useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import DOMPurify from 'dompurify';
import { BRAND_DARKER } from './healthWalletConstants';
import { useLazyGetNoteContentQuery } from './healthWalletSlice';

// ============================================================================
// DocumentBlock — ONE renderer for any stored clinical document, used two ways:
//
//   (a) NOTES feed — the narrative arrives INLINE with the page (get_patient_notes_page
//       already renders it), so nothing is fetched; binaries fetch base64 ON CLICK.
//   (b) PROCEDURE REPORTS — the drill-down emits IDS ONLY, so the body (and its
//       contentType) are unknown until fetched. The block fetches on mount, which —
//       because it only mounts inside an EXPANDED row — is the lazy-on-expand
//       behaviour we want. Bodies are 2.5-11KB each; the events payload stays lean.
//
// Both resolve through the SAME endpoint: get_patient_note_content(patient, id)
// accepts a FHIR note id OR a `documents.id` (branch 2, added 2026-08-16), so the
// existing note-content endpoint / relay / RTK query serve reports with no new plumbing.
// ============================================================================

// Narrative is XHTML from our CCDA mapper (controlled) OR external Kno2/Epic bundles (untrusted).
// ALWAYS sanitize before injecting — strips <script>, event-handler attrs, javascript: URLs, etc.
export const sanitize = (html: string): string => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });

// Plain-text projection (tags removed) — used to detect empty / boilerplate narrative.
export const toText = (html: string): string =>
  DOMPurify.sanitize(html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).replace(/\s+/g, ' ').trim();

// ★ GUARD 1 — length short-circuit. Every phrase this matches is < 50 chars, so anything past a few KB
// CANNOT be boilerplate; sanitising it to find that out is pure waste. Load-bearing for perf, not just tidiness:
// NotesSection's `visible` useMemo calls this for EVERY note and re-runs on every `notes` change, so without
// the guard a 1.6MB narrative gets a full DOMPurify pass per note-set change — one of the two full sanitises
// that froze the Notes tab (see technical_debt 2026-08-17). Only behaviour change: a >5KB document that somehow
// IS boilerplate now renders instead of being hidden — showing something is the safer failure.
const BOILERPLATE_MAX_CHARS = 5_000;

export const isBoilerplate = (html: string | null | undefined): boolean => {
  if (!html) return true;
  if (html.length > BOILERPLATE_MAX_CHARS) return false;
  const t = toText(html).toLowerCase();
  if (!t) return true;
  return /^(no information|no known [a-z ]*\.?|none\.?|information temporarily unavailable.*)$/.test(t);
};

// ★ GUARD 2 — never inject a huge narrative. Injecting ~1.6MB of HTML builds a DOM big enough to lock the tab
// (JS keeps running — it's layout/paint, not a loop). Above the threshold we sanitise + inject only a PREVIEW
// and offer the full document as a download, so the monster DOM is never built at all. A plain "show anyway"
// button would just relocate the freeze behind a click.
// Sizing (prod patient 1317af55, 1328 notes): only 18 exceed 200KB and 4 exceed 500KB, largest ~2.0M chars —
// so this fires on ~1.4% and is invisible in normal reading.
const LARGE_DOC_CHARS = 200_000;
const PREVIEW_CHARS = 50_000;

export const fmtDate = (d: string | null | undefined): string => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// ★ Some sources ship `text/html` that is really PLAIN TEXT: the only markup is <br>, and columns are aligned
// with runs of &nbsp; (verified on all 36 Epic procedure reports — zero <table>/<tr>/<pre>/<style>; total tag
// count == <br> count). &nbsp; doesn't collapse, so the spacing survives — but space-aligned columns only line
// up in a FIXED-WIDTH font. In the proportional body font every column drifts, which looks exactly like a table
// whose markup was stripped. Detected by CONTENT (no block-level tags) rather than by caller, so CCDA notes with
// genuine tables/divs keep the normal styling and only these text reports switch font.
// Deliberately NOT `white-space: pre-wrap`: these bodies use <br> for line breaks AND contain literal newlines,
// so pre-wrap would render every break twice.
export const isPreformattedText = (html: string): boolean =>
  !/<(table|div|p|ul|ol|h[1-6]|pre|blockquote)\b/i.test(html);

export const monoNarrativeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '0.78rem',
  lineHeight: 1.45,
  color: '#374151',
  overflowX: 'auto',
} as const;

// Shared styling so source-narrative tables/lists stay readable and never overflow the card.
export const narrativeSx = {
  fontSize: '0.85rem',
  color: '#374151',
  overflowX: 'auto',
  '& table': { borderCollapse: 'collapse', width: '100%', my: 0.5 },
  '& th, & td': { border: '1px solid #e5e7eb', px: 0.75, py: 0.5, textAlign: 'left', verticalAlign: 'top' },
  '& th': { bgcolor: '#f7f7fa', fontWeight: 600 },
  '& ul': { pl: 2.5, my: 0.5 },
  '& p': { my: 0.5 },
} as const;

// ---------------------------------------------------------------------------
// CIRCUIT BREAKER — caps fetches per document id. MODULE-level on purpose: a per-instance ref cannot stop a
// loop caused by the component REMOUNTING, which resets the ref every time. Kept after the 2026-08-16 freeze
// was fixed: the original loop happened because `get_patient_note_content` returns NULL for an unresolvable
// id, so `data` was null, `isFetching`/`isError` both false, and the effect re-fired on every render. Cheap
// insurance that no future edge can spin.
// ---------------------------------------------------------------------------
const MAX_FETCHES_PER_ID = 3;
const fetchCounts = new Map<string, number>();
const loopReported = new Set<string>();

const triggerDownload = (href: string, filename: string, revoke?: string) => {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  if (revoke) URL.revokeObjectURL(revoke);
};

/** Trigger a browser download of base64 content without persisting anything. */
const downloadBase64 = (data: string, contentType: string | null | undefined, filename: string) =>
  triggerDownload(`data:${contentType || 'application/octet-stream'};base64,${data}`, filename);

/** Download an already-in-hand TEXT body (the large-document path — no refetch, and no data: URL length limit). */
const downloadText = (text: string, filename: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/html' }));
  triggerDownload(url, filename.endsWith('.html') ? filename : `${filename}.html`, url);
};

export interface DocumentBlockProps {
  /** FHIR note id (notes feed) OR documents.id (procedure report) — both resolve via get_patient_note_content. */
  id: string;
  title?: string | null;
  /** Small line under the title, e.g. "Pathology · Mar 23, 2026". */
  subtitle?: string | null;
  /** Present when the caller already has the body (notes feed). Absent ⇒ fetch on mount. */
  narrative?: string | null;
  /** Known up-front for notes. Undefined ⇒ derive from the fetched contentType. */
  hasBinary?: boolean;
  /** Visual weight — notes are standalone cards; reports nest inside an already-bordered row. */
  variant?: 'card' | 'nested';
}

// memo: the notes navigator recomputes on every scroll frame → without this the block would
// re-render (and re-run DOMPurify over the whole narrative) each frame.
export const DocumentBlock: FC<DocumentBlockProps> = memo(
  ({ id, title, subtitle, narrative, hasBinary, variant = 'card' }) => {
    const [fetchContent, { data: content, isFetching, isError }] = useLazyGetNoteContentQuery();
    const [downloadRequested, setDownloadRequested] = useState(false);

    const label = title || 'Document';
    const hasInlineText = narrative != null && !isBoilerplate(narrative);
    // The caller KNOWS this one has nothing to show: it supplied a narrative (or explicitly `hasBinary:false`)
    // and the narrative is empty/boilerplate. That's the notes feed — never fetch, just render nothing.
    // `hasBinary === undefined` means the caller doesn't know (reports, ids-only) → we must fetch to find out.
    const knownEmpty = !hasInlineText && hasBinary === false;
    // Nothing local to show and the caller can't tell us ⇒ fetch before we can render, or even decide HOW to
    // render (with ids-only, contentType is unknown until the body arrives).
    const mustFetch = !hasInlineText && hasBinary === undefined && !knownEmpty;

    // Fire AT MOST ONCE per id. Guarding on `!content` instead would spin forever whenever the fetch resolves
    // to null — get_patient_note_content returns NULL for an unresolvable id, so `data` is null, `isFetching`
    // is false and `isError` is false, and the effect re-fires on every render. That froze the Notes tab.
    const triedRef = useRef<string | null>(null);

    useEffect(() => {
      if (!mustFetch || triedRef.current === id) return;

      // CIRCUIT BREAKER — module-level, so REMOUNTS can't reset it.
      const n = (fetchCounts.get(id) ?? 0) + 1;
      fetchCounts.set(id, n);
      if (n > MAX_FETCHES_PER_ID) {
        if (!loopReported.has(id)) {
          loopReported.add(id);
          // eslint-disable-next-line no-console
          console.error('[DocumentBlock] LOOP GUARD — refusing further fetches', {
            id, attempts: n, title, hasBinary, hasInlineText, knownEmpty, mustFetch,
          });
        }
        return;
      }

      triedRef.current = id;
      fetchContent({ id });
    }, [mustFetch, id, fetchContent, title, hasBinary, hasInlineText, knownEmpty]);

    useEffect(() => {
      if (downloadRequested && content?.data) {
        downloadBase64(content.data, content.contentType, label);
        setDownloadRequested(false);
      }
    }, [downloadRequested, content, label]);

    const handleDownload = () => {
      setDownloadRequested(true);
      if (!content) fetchContent({ id });
    };

    // Fetched body: inline when textual, else a download.
    const fetchedText = content?.narrative && !isBoilerplate(content.narrative) ? content.narrative : null;
    const body = hasInlineText ? narrative : fetchedText;
    // Space-aligned plain-text documents need a fixed-width font or their columns drift; everything with real
    // block markup keeps the normal narrative styling. See isPreformattedText.
    const bodySx = body && isPreformattedText(body) ? monoNarrativeSx : narrativeSx;
    // Over the size guard ⇒ only a preview is rendered; drives the header's "Long document" caption + Download.
    const isTruncated = !!body && body.length > LARGE_DOC_CHARS;

    // A note that is all-boilerplate text and not a binary → nothing worth a card (the old NoteBlock's
    // early bail, restored: it must happen BEFORE any fetch, not after one comes back empty).
    if (knownEmpty) return null;
    // Ids-only (a report) whose fetch came back with neither text nor bytes → nothing to show either.
    if (mustFetch && !isFetching && content !== undefined && !fetchedText && !content?.data) return null;

    const shell =
      variant === 'nested'
        ? { p: 0, mt: 1.5, borderRadius: 0, border: 'none', bgcolor: 'transparent', boxShadow: 'none' }
        : {
            p: { xs: 2, sm: 2.5 },
            mb: 3,
            borderRadius: 3,
            border: '1px solid #d7d3f0',
            bgcolor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          };

    return (
      <Paper elevation={0} sx={shell}>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, pb: 1.5,
            borderBottom: '1px solid #efeaff',
          }}
        >
          {/* Truncated documents get a distinct icon colour — a glanceable signal that this card isn't the whole
              document, reinforcing the caption below without another line of text. Muted red (not the harsher
              #bd352b used for abnormal clinical flags): this is a display limitation, not a clinical warning. */}
          <DescriptionIcon sx={{ color: isTruncated ? '#c0736b' : BRAND_DARKER, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1c1c1e' }}>
              {label}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
            {/* Truncation lives WITH the document's identity (title · type · date) rather than as a banner over
                the content — it's a property of this document, and the header is where the reader is already
                looking. Below the content it was only discoverable after scrolling the whole fragment. */}
            {isTruncated ? (
              // Caption + Download on ONE row, inside the text column. As a sibling of that column the button
              // sat at the far right of the header, visually detached from the sentence it belongs to (and it
              // disappeared entirely in a narrow card). Keeping them together also lets the pair wrap as a unit.
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: '#8e8e93' }}>
                  Long document — showing the beginning only.
                </Typography>
                {body ? (
                  <Button
                    size="small"
                    onClick={() => downloadText(body, label)}
                    sx={{ textTransform: 'none', color: BRAND_DARKER, fontWeight: 600, minWidth: 0, px: 0.75, py: 0 }}
                  >
                    Download full
                  </Button>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Box>

        {isFetching && !body ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} sx={{ color: BRAND_DARKER }} />
          </Box>
        ) : isError ? (
          <Typography variant="body2" color="text.secondary">
            This document couldn’t be loaded.
          </Typography>
        ) : body && body.length > LARGE_DOC_CHARS ? (
          // ★ GUARD 2 — oversized document: slice BEFORE sanitising (so DOMPurify never sees the full body)
          // and inject only the preview. DOMPurify repairs the tags the slice cuts through, so a mid-element
          // truncation is safe. Full text stays one click away as a download, built from the body we already
          // hold — no refetch, and a Blob avoids the length ceiling a data: URL would hit at this size.
          <>
            {/* The truncation notice + Download live in the HEADER above (with the document's identity), so the
                content area is just the preview — no banner competing with the note itself. */}
            <Box sx={bodySx} dangerouslySetInnerHTML={{ __html: sanitize(body.slice(0, PREVIEW_CHARS)) }} />
            {/* Fade at the cut so the boundary reads as intentional rather than as text that just stops. */}
            <Box sx={{ height: 48, mt: -6, position: 'relative', background: 'linear-gradient(transparent, white)' }} />
          </>
        ) : body ? (
          <Box sx={bodySx} dangerouslySetInnerHTML={{ __html: sanitize(body) }} />
        ) : (
          // Binary (PDF/image) — no inline preview; download the base64.
          <Button
            onClick={handleDownload}
            disabled={isFetching}
            startIcon={isFetching ? <CircularProgress size={16} sx={{ color: BRAND_DARKER }} /> : undefined}
            sx={{ textTransform: 'none', color: BRAND_DARKER, fontWeight: 600 }}
          >
            {isFetching ? 'Preparing…' : `Download ${content?.contentType?.split('/')[1] || 'document'}`}
          </Button>
        )}
      </Paper>
    );
  },
);
